// src/components/lettings/MarketingForm.tsx — "the form itself" (prototype §40 marketFormHTML): every Rightmove / Zoopla
// field, grouped as the prototype groups them — The advert · The property · Who it suits · The words · Pictures · Links ·
// The portals — with the readiness list on top and "See exactly what would be sent". Task L.
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Segmented, Button, Field, Input, Modal, Select, Textarea, SkeletonRows } from '../ui';
import { FormError } from '../forms/common';
import { ReadinessList, Section } from './bits';
import { FlatPhotoStrip } from '../media/FlatPhotoStrip';
import { lettingsApi, type Marketing, type Pair } from '../../lib/lettings';

type F = Record<string, any>;
const MONEY = new Set(['advertisedRentMinor', 'depositMinor', 'holdingDepositMinor']);
const NUM = new Set(['minTermMonths', 'bathrooms', 'receptionRooms', 'floorArea', 'epcCurrent', 'epcPotential']);
const BOOL = new Set(['newBuild', 'petsAllowed', 'smokingAllowed', 'studentsAllowed', 'housingBenefitAllowed', 'billsIncluded']);

/** Form strings from the record: money in pounds, yes/no/ask for the booleans, features one a line. */
function toForm(f: F): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (MONEY.has(k)) out[k] = v == null ? '' : String(Number(v) / 100);
    else if (BOOL.has(k)) out[k] = v === true ? 'yes' : v === false ? 'no' : '';
    else if (k === 'features') out[k] = (v as string[]).join('\n');
    else out[k] = v == null ? '' : String(v);
  }
  return out;
}
function fromForm(k: string, v: string): unknown {
  if (MONEY.has(k)) return v.trim() === '' ? null : Math.round(Number(v.replace(/[£,\s]/g, '')) * 100);
  if (NUM.has(k)) return v.trim() === '' ? null : Number(v);
  if (BOOL.has(k)) return v === 'yes' ? true : v === 'no' ? false : null;
  if (k === 'features') return v.split('\n').map((x) => x.trim()).filter(Boolean);
  return v.trim() === '' ? null : v;
}

export function MarketingForm({ m }: { m: Marketing }) {
  const qc = useQueryClient();
  const initial = useMemo(() => toForm(m.fields), [m.fields]);
  const [form, setForm] = useState(initial);
  const [peek, setPeek] = useState(false);
  const ro = !m.can.update;
  const dirty = Object.keys(form).filter((k) => form[k] !== initial[k] && k !== 'defaultDisplayAddress' && k !== 'bedrooms' && k !== 'currency' && k !== 'rentPeriod');
  const save = useMutation({
    mutationFn: () => lettingsApi.saveMarketing(m.listingId, Object.fromEntries(dirty.map((k) => [k, fromForm(k, form[k] ?? '')]))),
    onSuccess: (r) => { qc.setQueryData(['marketing', m.listingId], r.data); void qc.invalidateQueries({ queryKey: ['marketing', m.listingId] }); },
  });
  const o = m.options;
  const set = (k: string) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const text = (k: string, label: string, ph = '', wide = false, hint?: string) => <Cell wide={wide}><Field label={label} hint={hint}><Input value={form[k] ?? ''} onChange={set(k)} placeholder={ph} disabled={ro} className="w-full" /></Field></Cell>;
  const pick = (k: string, label: string, opts: readonly Pair[] | readonly string[], none?: string) => (
    <Cell><Field label={label}><Select value={form[k] ?? ''} onChange={set(k)} disabled={ro} className="w-full">
      {none !== undefined && <option value="">{none}</option>}
      {opts.map((x) => typeof x === 'string' ? <option key={x} value={x}>{x}</option> : <option key={x[0]} value={x[0]}>{x[1]}</option>)}
    </Select></Field></Cell>
  );
  const yna = (k: string, label: string) => pick(k, label, [['yes', 'Yes'], ['no', 'No']], 'Ask us');
  const features = (form.features ?? '').split('\n').filter((x) => x.trim()).length;

  return (
    <div>
      <div className="surface p-4">
        {m.readiness.done
          ? <p className="mb-3 text-[13px] font-medium text-emerald-700">Everything the portals ask for is here.{m.status === 'PUBLISHED' ? '' : ' Publish it to put it on the market.'}</p>
          : <p className="mb-3 text-[13px] font-medium text-amber-700">Before it can go live</p>}
        <ReadinessList readiness={m.readiness} />
      </div>

      <Section title="The advert">
        <Grid>
          {text('advertisedRentMinor', 'Asking rent (£)', '£ per month')}
          {pick('priceQualifier', 'Quoted as', o.priceQualifiers)}
          {text('depositMinor', 'Deposit (£)', 'five weeks rent')}
          {text('holdingDepositMinor', 'Holding deposit (£)', 'one week')}
          <Cell><Field label="Available from"><Input type="date" value={form.availableFrom ?? ''} onChange={set('availableFrom')} disabled={ro} className="w-full" /></Field></Cell>
          {text('minTermMonths', 'Minimum term, months')}
          {pick('letType', 'Kind of let', o.letTypes)}
          {pick('furnished', 'Furnished', o.furnished, '—')}
          {text('displayAddress', 'Display address', m.fields.defaultDisplayAddress ?? 'What the public sees — the building and the postcode area', true)}
        </Grid>
      </Section>

      <Section title="The property">
        <Grid>
          {pick('propertyType', 'Type', o.propertyTypes, '—')}
          <Cell><Field label="Bedrooms" hint="From the flat record"><Input value={m.fields.bedrooms ?? ''} disabled className="w-full" /></Field></Cell>
          {text('bathrooms', 'Bathrooms')}
          {text('receptionRooms', 'Reception rooms')}
          {text('floorArea', 'Floor area')}
          {pick('areaUnit', 'Measured in', o.areaUnits)}
          {text('floorLevel', 'Floor', 'Second')}
          {pick('outsideSpace', 'Outside space', o.outsideSpace, 'None')}
          {pick('parking', 'Parking', o.parking, 'None')}
          {pick('councilTaxBand', 'Council tax band', o.councilTaxBands, '—')}
          {pick('epcRating', 'EPC rating', o.epcBands, '—')}
          {text('epcCurrent', 'EPC score now', '81')}
          {text('epcPotential', 'EPC potential', '86')}
          {yna('newBuild', 'Newly built')}
          {text('epcUrl', 'EPC register link', 'https://find-energy-certificate.service.gov.uk/…', true)}
        </Grid>
      </Section>

      <Section title="Who it suits">
        <p className="mb-3 text-[12px] text-ink-500">Both portals let people filter on these, so an honest answer saves a wasted viewing. Discrimination law applies — a blanket refusal of benefits claimants or families with children is unlawful, so "ask us" is usually the right answer.</p>
        <Grid>
          {yna('petsAllowed', 'Pets')}
          {yna('smokingAllowed', 'Smoking')}
          {yna('studentsAllowed', 'Students')}
          {yna('housingBenefitAllowed', 'Housing benefit')}
          {yna('billsIncluded', 'Bills included')}
          {text('billsDetail', 'Which bills', 'Water and broadband')}
          {text('accessibility', 'Step-free access and lifts', 'Lift to every floor, level threshold at the entrance', true)}
        </Grid>
      </Section>

      <Section title="The words">
        <Grid>
          <Cell wide><Field label="Headline" hint={`${(form.headline ?? '').length} characters — both portals cut it around 100.`}><Input value={form.headline ?? ''} onChange={set('headline')} disabled={ro} className="w-full" /></Field></Cell>
          <Cell wide><Field label="Description" hint={`${(form.description ?? '').length} characters.`}><Textarea rows={7} value={form.description ?? ''} onChange={set('description')} disabled={ro} placeholder="Write it as you would say it. The rooms, the light, the building, the area, the transport." className="w-full" /></Field></Cell>
          <Cell wide><Field label="Key features, one a line" hint={`${features} of ${o.maxFeaturesShown} — Rightmove shows the first ten.`}><Textarea rows={5} value={form.features ?? ''} onChange={set('features')} disabled={ro} placeholder={'Balcony\nLift\nBike store'} className="w-full" /></Field></Cell>
          {text('viewingArrangements', 'Viewing arrangements', 'Accompanied viewings, weekdays and Saturday mornings', true)}
        </Grid>
      </Section>

      {!ro && <SaveBar dirty={dirty.length} pending={save.isPending} onSave={() => save.mutate()} onReset={() => setForm(initial)} error={save.error} />}

      <Section title="Pictures"><FlatPhotoStrip unitId={m.unit.id} propertyId={m.property.id} /></Section>

      <Section title="Links">
        <Grid>
          {text('virtualTourUrl', 'Virtual tour', 'https://', true)}
          {text('videoUrl', 'Video', 'https://', true)}
          {text('brochureUrl', 'Brochure', 'https://', true)}
        </Grid>
      </Section>

      <Section title="The portals">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-ink-500">Reference</span><span className="rounded-full bg-bronze-50 px-2.5 py-1 font-medium text-bronze-700">{m.portals.reference}</span>
          <span className="rounded-full border border-ink-200 px-2.5 py-1 text-ink-600">Rightmove · never sent</span>
          <span className="rounded-full border border-ink-200 px-2.5 py-1 text-ink-600">Zoopla · never sent</span>
        </div>
        <p className="mt-2 text-[12px] text-ink-500">Not connected yet. Everything here is held ready — turn the feeds on under Settings → Integrations and press send. (REQUIRES EXTERNAL CREDENTIAL)</p>
        <button onClick={() => setPeek(true)} className="mt-2 text-[12.5px] font-medium text-bronze-600 hover:underline">See exactly what would be sent</button>
      </Section>

      {!ro && dirty.length > 0 && <div className="sticky bottom-3 mt-6"><SaveBar dirty={dirty.length} pending={save.isPending} onSave={() => save.mutate()} onReset={() => setForm(initial)} error={save.error} /></div>}
      {peek && <PayloadPeek listingId={m.listingId} onClose={() => setPeek(false)} />}
    </div>
  );
}

const Grid = ({ children }: { children: ReactNode }) => <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
const Cell = ({ children, wide }: { children: ReactNode; wide?: boolean }) => <div className={wide ? 'sm:col-span-2 lg:col-span-3' : ''}>{children}</div>;
function SaveBar({ dirty, pending, onSave, onReset, error }: { dirty: number; pending: boolean; onSave: () => void; onReset: () => void; error: unknown }) {
  return (
    <div className="mt-5 rounded-xl border border-ink-100 bg-white/95 p-3 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1 text-[12.5px] text-ink-500">{dirty ? `${dirty} change${dirty === 1 ? '' : 's'} not saved` : 'All saved'}</span>
        <Button variant="ghost" size="sm" onClick={onReset} disabled={!dirty || pending}>Undo</Button>
        <Button size="sm" onClick={onSave} disabled={!dirty || pending}>{pending ? 'Saving…' : 'Save'}</Button>
      </div>
      <div className="mt-2 empty:hidden"><FormError error={error} /></div>
    </div>
  );
}

/** "What would be sent" (prototype payloadPeek): the same record in each portal's own shape. */
export function PayloadPeek({ listingId, onClose }: { listingId: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['payload', listingId], queryFn: () => lettingsApi.payload(listingId) });
  const [which, setWhich] = useState<'rightmove' | 'zoopla'>('rightmove');
  const body = q.data ? JSON.stringify(q.data[which], null, 2) : '';
  return (
    <Modal open onClose={onClose} size="lg" title="What would be sent"
      footer={<><Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(body)}>Copy it</Button><Button onClick={onClose}>Close</Button></>}>
      <p className="text-[12.5px] text-ink-600">The same marketing record in each portal's own shape. This is what the server will post when the feeds are switched on.{q.data && !q.data.ready ? ` Not ready: ${q.data.missing.join('; ')}.` : ''}</p>
      <Segmented className="mt-3" value={which} onChange={setWhich} items={[{ key: 'rightmove' as const, label: 'Rightmove' }, { key: 'zoopla' as const, label: 'Zoopla' }]} />
      <pre className="mt-3 max-h-[50vh] overflow-auto rounded-lg border border-ink-200 bg-ink-50 p-4 text-[11.5px] leading-relaxed text-ink-800">{q.isLoading ? <SkeletonRows rows={8} cols={1} /> : body}</pre>
      {q.data && <p className="mt-2 text-[11.5px] text-ink-400">{q.data.note}</p>}
    </Modal>
  );
}
